// ──────────────────────────────────────────────
// DeviceBridgeService — Bridges mic device changes between
// RecordingService and DailyCallService.
//
// The recording pipeline captures the mic RAW (echoCancellation /
// noiseSuppression / autoGainControl all off — see StreamAcquirer
// DEFAULT_CONSTRAINTS) for transcription fidelity. That raw capture
// must never be what Daily transmits: without echo cancellation the
// mic re-captures everything the provider's speakers play — i.e. the
// patient's own voice — and sends it back, so the patient hears an
// echo of every word they say.
//
// The bridge therefore opens a second, processed (AEC/NS/AGC on)
// capture of the same physical device for the call, while the
// recording pipeline keeps the raw original. Browsers share the
// hardware open between the two captures. If the processed
// acquisition fails we fall back to cloning the raw track — degraded
// (far-end echo returns) but the call keeps working.
//
// Phase 2 of Daily.co telehealth integration.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import type { DailyCallService } from './DailyCallService';

const SENTRY_CATEGORY = 'devicebridge.service';

/** Standard WebRTC call processing — what Daily would request for its own capture. */
const CALL_TRACK_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
} as const;

interface AcquiredCallTrack {
  track: MediaStreamTrack;
  /** False when we fell back to cloning the raw (unprocessed) source track. */
  processed: boolean;
}

export class DeviceBridgeService {
  private dailyCallService: DailyCallService;
  private currentCallTrack: MediaStreamTrack | null = null;
  private switchQueue: Promise<void> = Promise.resolve();

  constructor(dailyCallService: DailyCallService) {
    this.dailyCallService = dailyCallService;
    dailyCallService.setCustomTrackProvider(() => this.getCurrentCallTrack());
  }

  /**
   * Called once during session setup. Acquires a processed call track for the
   * source track's device, stores the reference, and returns it for the caller
   * to pass to DailyCallService.initialize({ audioSource }) / setAudioDevice().
   */
  async createInitialCallTrack(sourceTrack: MediaStreamTrack): Promise<MediaStreamTrack> {
    const { track, processed } = await this.acquireCallTrack(sourceTrack);
    this.currentCallTrack = track;

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Initial call track created',
      level: 'info',
      data: {
        sourceTrackId: sourceTrack.id,
        callTrackId: track.id,
        processed,
      },
    });

    return track;
  }

  /**
   * Called on device switch. Serialized via switchQueue so concurrent
   * switches execute in order — only the final device ends up active.
   */
  async handleDeviceSwitch(newSourceTrack: MediaStreamTrack): Promise<void> {
    this.switchQueue = this.switchQueue.then(() => this.doSwitch(newSourceTrack));
    return this.switchQueue;
  }

  /** Exposes the current call track — used by DailyCallService's unmute guard. */
  getCurrentCallTrack(): MediaStreamTrack | null {
    return this.currentCallTrack;
  }

  /** Stop current call track, null references, clear provider. */
  dispose(): void {
    if (this.currentCallTrack) {
      this.currentCallTrack.stop();

      SentryService.addBreadcrumb({
        category: SENTRY_CATEGORY,
        message: 'Call track stopped on dispose',
        level: 'info',
        data: { callTrackId: this.currentCallTrack.id },
      });
    }

    this.currentCallTrack = null;
    this.dailyCallService.setCustomTrackProvider(null);
  }

  // ── Private ──

  /**
   * Acquire the track Daily should transmit for the given source track's
   * device: a fresh processed capture, or a raw clone as last resort.
   * Throws only if both paths fail (e.g. the source track is ended).
   */
  private async acquireCallTrack(sourceTrack: MediaStreamTrack): Promise<AcquiredCallTrack> {
    const deviceId = sourceTrack.getSettings().deviceId;

    if (deviceId && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
            ...CALL_TRACK_CONSTRAINTS,
          },
          video: false,
        });

        const track = stream.getAudioTracks()[0];
        if (track) {
          return { track, processed: true };
        }
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        SentryService.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { source: 'devicebridge_processed_acquire_failed' },
          extra: { sourceTrackId: sourceTrack.id },
        });
      }
    }

    // Fallback: clone the raw source track. The call loses echo cancellation,
    // but transmits the correct device.
    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Processed capture unavailable — falling back to raw clone',
      level: 'warning',
      data: { sourceTrackId: sourceTrack.id },
    });
    return { track: sourceTrack.clone(), processed: false };
  }

  private async doSwitch(newSourceTrack: MediaStreamTrack): Promise<void> {
    let acquired: AcquiredCallTrack;

    try {
      acquired = await this.acquireCallTrack(newSourceTrack);
    } catch (err) {
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: 'devicebridge_call_track_failed' },
        extra: { sourceTrackId: newSourceTrack.id },
      });
      return;
    }

    const oldTrack = this.currentCallTrack;
    if (oldTrack) {
      oldTrack.stop();
    }

    this.currentCallTrack = acquired.track;

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Device switch — call track replaced',
      level: 'info',
      data: {
        oldTrackId: oldTrack?.id ?? null,
        newTrackId: acquired.track.id,
        processed: acquired.processed,
      },
    });

    try {
      await this.dailyCallService.setAudioDevice(acquired.track);
    } catch (err) {
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: 'devicebridge_set_audio_failed' },
        extra: { callTrackId: acquired.track.id },
      });
      // Do NOT rethrow — recording pipeline is unaffected
    }
  }
}
